import { Controller } from "@hotwired/stimulus";

// ===========================================================
// Helper functions
// ===========================================================

function roundToNearestThousand(number) {
  return Math.round(number/1000) * 1000;
}

function roundToNearestHundred(number) {
  return Math.round(number/100) * 100;
}

function roundToStep(num, step) {
  return roundToNearestHundred(Math.round(num / step) * step);
}

export default class extends Controller {
  static targets = ["formField",
  "personalPropertyInput", "valuableItemsInput", "deductibleInput",
  "valuableItemsMinLabel", "valuableItemsMaxLabel", "valuableItemsDisplay",
  "personalPropertyMinLabel", "personalPropertyMaxLabel", "personalPropertyDisplay",
  "deductibleDisplay",
  ];

  static values = { rooms: Number,
    personalProperty: Number,
    valuableItems: Number
  };

  connect() {
    this.initSliders();
  }

  // ===========================================================
  // Logic for calculating min, max and step for sliders
  // ===========================================================

  minAndMaxPersonalProperty() {
    const rooms = this.roomsValue;

    const personalPropertyRanges = {
      "1": { min: 6000, max: 17000 },
      "2": { min: 9000, max: 27000 },
      "3": { min: 15000, max: 45000 },
      "4": { min: 21000, max: 62000 },
      "5": { min: 26000, max: 77000 },
      "6": { min: 31000, max: 93000 },
      "7": { min: 36000, max: 107000 },
      "8": { min: 40000, max: 119000 },
      "9": { min: 43000, max: 128000 },
      "10": { min: 46000, max: 140000 }
    };

    return personalPropertyRanges[rooms.toString()];
  }

  maxValuableItems() {
    const max = roundToNearestHundred(this.personalPropertyInputTarget.value / 4);
    return max > 20000 ? 20000 : max;
  }

  stepPersonalProperty() {
    const personalProperty = this.minAndMaxPersonalProperty();
    return (personalProperty.max - personalProperty.min) / 6;
  }

  stepValuableItems() {
    return (this.maxValuableItems() - 400) / 6;
  }

  CoverageAmountsHash() {
    const jsonData = {};

    jsonData['valuable_items'] = parseInt(this.valuableItemsInputTarget.value)
    jsonData['personal_property'] = parseInt(this.personalPropertyInputTarget.value)
    return jsonData;
  }

// ===========================================================
// Actions
// ===========================================================

  updateFormField() {
    this.formFieldTarget.value = JSON.stringify(this.CoverageAmountsHash());
    this.formFieldTarget.dispatchEvent(new Event('range-jump', { 'bubbles': true }));
  }

  initSliders() {
    const personalPropertyRange = this.minAndMaxPersonalProperty();
    this.personalPropertyInputTarget.setAttribute("min", personalPropertyRange.min);
    this.personalPropertyInputTarget.setAttribute("max", personalPropertyRange.max);
    // When data attribute is empty, Stimulus Number value defaults to 0; prefer the input's server-rendered value or min
    const initialPersonal = (this.personalPropertyValue >= personalPropertyRange.min)
      ? this.personalPropertyValue
      : (parseInt(this.personalPropertyInputTarget.value, 10) || personalPropertyRange.min);
    this.personalPropertyInputTarget.setAttribute("value", initialPersonal);
    this.jumpToNearestStep(initialPersonal, null);

    const valuableItemsMax = this.maxValuableItems();
    this.valuableItemsInputTarget.setAttribute("min", 0);
    this.valuableItemsInputTarget.setAttribute("max", valuableItemsMax);
    // Same for valuable items: avoid overwriting with 0 when data attribute is empty
    const initialValuable = (this.valuableItemsValue > 0)
      ? this.valuableItemsValue
      : (parseInt(this.valuableItemsInputTarget.value, 10) || 0);
    this.valuableItemsInputTarget.setAttribute("value", initialValuable);
    this.jumpToNearestStep(null, initialValuable);

    this.refreshLabels();
    this.updateFormField();
  }

  updateSliders() {
    // Called on user change event: use current slider values, not data attributes
    const valuableItemsMax = this.maxValuableItems();
    this.valuableItemsInputTarget.setAttribute("max", valuableItemsMax);

    const currentPersonal = parseInt(this.personalPropertyInputTarget.value, 10);
    const currentValuable = parseInt(this.valuableItemsInputTarget.value, 10);
    this.jumpToNearestStep(currentPersonal, currentValuable || null);

    this.refreshLabels();
    this.updateFormField();
  }

  refreshLabels() {
    const personalPropertyRange = this.minAndMaxPersonalProperty();
    const valuableItemsMax = this.maxValuableItems();

    this.personalPropertyMinLabelTarget.textContent = personalPropertyRange.min.toLocaleString();
    this.personalPropertyMaxLabelTarget.textContent = personalPropertyRange.max.toLocaleString();
    this.personalPropertyDisplayTarget.textContent = this.CoverageAmountsHash()['personal_property'].toLocaleString();
    this.valuableItemsMinLabelTarget.textContent = 0;
    this.valuableItemsMaxLabelTarget.textContent = valuableItemsMax.toLocaleString();
    this.valuableItemsDisplayTarget.textContent = this.CoverageAmountsHash()['valuable_items'].toLocaleString();
  }

  jumpToNearestStep(personalPropertyValue, valuableItemsValue) {
    const personalPropertyStep = this.stepPersonalProperty();
    const valuableItemsStep = this.stepValuableItems();
    const minPersonalProperty = this.minAndMaxPersonalProperty().min;

    if (personalPropertyValue) {
      this.personalPropertyInputTarget.value = minPersonalProperty + roundToStep(personalPropertyValue - minPersonalProperty, personalPropertyStep);
    }

    if (valuableItemsValue) {
      if (valuableItemsValue < 200) {
        this.valuableItemsInputTarget.value = 0
      } else {
        this.valuableItemsInputTarget.value = 400 + roundToStep(valuableItemsValue - 400, valuableItemsStep);
      }
    }
  }

  updateTicks() {
    document.querySelectorAll(".personalPropertyTick").forEach((el, i) => {
      const min = parseInt(this.personalPropertyInputTarget.min)
      const max = parseInt(this.personalPropertyInputTarget.max)
      const step = this.stepPersonalProperty()
      console.log(min + (max - min)/7 * i);
      console.log((max - min)/7);
      // la formule ci dessous est pas tout à fait bonne, par exemple pour un 3 pièces, le dernier ticks est à 40.000 au lieu de 45.000
      el.innerText = min + roundToStep((max - min)/7 * i, step)
    });
  }
}
